import type { Fact } from '../engine/facts';

export function expr(fact: Fact): string {
  return `${fact.left} ${fact.op === 'mul' ? '×' : '÷'} ${fact.right}`;
}

export function seconds(ms: number | null): string {
  if (ms === null || !Number.isFinite(ms)) return '—';
  return `${(ms / 1000).toFixed(1).replace('.', ',')} с`;
}

export function percent(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '—';
  return `${Math.round(value)} %`;
}

export function duration(ms: number): string {
  /** Как и у соседей: нечисло и отрицательная длительность дают «—», а не
   *  «NaN мин NaN с». Значение приходит из строки в IndexedDB, которую мог
   *  записать другой билд, и раздел 6.4 требует, чтобы `NaN` и `Infinity`
   *  на экран не попадали. */
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const total = Math.round(ms / 1000);
  const min = Math.floor(total / 60);
  const sec = total % 60;
  return min > 0 ? `${min} мин ${sec} с` : `${sec} с`;
}

/** Дата и время сессии. Язык пока зашит: локализация в этот заход не
 *  входит, и `undefined` вместо метки отдал бы язык системы — англоязычную
 *  дату посреди русского экрана.
 *
 *  Год появляется только тогда, когда он отличается от текущего. Совсем без
 *  года две сессии с разницей ровно в год неразличимы в истории, а
 *  приложение рассчитано на годы занятий; но дописывать «2026» к вчерашней
 *  тренировке — шум на экране, который ребёнок читает каждый день. */
export function dateTime(ms: number, nowMs: number = Date.now()): string {
  const date = new Date(ms);
  const sameYear = date.getFullYear() === new Date(nowMs).getFullYear();
  return new Intl.DateTimeFormat('ru', {
    day: 'numeric',
    month: 'long',
    year: sameYear ? undefined : 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

export function modeLabel(mode: 'mul' | 'div' | 'mixed'): string {
  if (mode === 'mul') return 'умножение';
  if (mode === 'div') return 'деление';
  return 'умножение и деление';
}

export function tilesLabel(tiles: readonly number[]): string {
  return [...tiles].sort((a, b) => a - b).map((t) => `×${t}`).join(' ');
}

/** Русские числительные. Полноценная локализация в этот заход не входит, но
 *  «3 заданий · 1 ошибок» читает ребёнок 7–11 лет, который сам ещё учится
 *  читать, и такая строка учит его неправильному языку.
 *
 *  Категорий у русского четыре, а не три: `other` срабатывает на дробных
 *  значениях. Здесь считаются только целые, поэтому `other` сведён к
 *  `many` — но сведён явно, а не забыт. */
const PLURAL = new Intl.PluralRules('ru');

export function plural(count: number, one: string, few: string, many: string): string {
  const form = PLURAL.select(count);
  if (form === 'one') return one;
  if (form === 'few') return few;
  return many;
}
